export type SupervisorOrderMode =
  | 'alphabetical'
  | 'status'
  | 'custom'
  | 'openDateAsc'
  | 'openDateDesc'
  | 'lastChangedDateAsc'
  | 'lastChangedDateDesc';

export const supervisorOrderModeValues: readonly SupervisorOrderMode[] = [
  'alphabetical',
  'status',
  'custom',
  'openDateAsc',
  'openDateDesc',
  'lastChangedDateAsc',
  'lastChangedDateDesc',
];

export const supervisorDateOrderOptions: readonly {
  label: string;
  value: SupervisorOrderMode;
}[] = [
  { label: 'Open Date: Oldest First', value: 'openDateAsc' },
  { label: 'Open Date: Newest First', value: 'openDateDesc' },
  { label: 'Last Change Date: Oldest First', value: 'lastChangedDateAsc' },
  { label: 'Last Change Date: Newest First', value: 'lastChangedDateDesc' },
];

export function sortItemsBySupervisorDateOrder<T>(
  items: T[],
  orderMode: SupervisorOrderMode,
  getOpenDate: (item: T) => string | null | undefined,
  getLastChangedDate: (item: T) => string | null | undefined
): boolean {
  if (orderMode === 'openDateAsc') {
    items.sort((a, b) => compareDateValues(getOpenDate(a), getOpenDate(b), 'asc'));
    return true;
  }

  if (orderMode === 'openDateDesc') {
    items.sort((a, b) => compareDateValues(getOpenDate(a), getOpenDate(b), 'desc'));
    return true;
  }

  if (orderMode === 'lastChangedDateAsc') {
    items.sort((a, b) => compareDateValues(getLastChangedDate(a), getLastChangedDate(b), 'asc'));
    return true;
  }

  if (orderMode === 'lastChangedDateDesc') {
    items.sort((a, b) => compareDateValues(getLastChangedDate(a), getLastChangedDate(b), 'desc'));
    return true;
  }

  return false;
}

function compareDateValues(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: 'asc' | 'desc'
): number {
  const aTimestamp = toTimestamp(a);
  const bTimestamp = toTimestamp(b);

  if (aTimestamp === null && bTimestamp === null) {
    return 0;
  }

  if (aTimestamp === null) {
    return 1;
  }

  if (bTimestamp === null) {
    return -1;
  }

  return direction === 'asc'
    ? aTimestamp - bTimestamp
    : bTimestamp - aTimestamp;
}

function toTimestamp(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }

  const jsonDateMatch = /\/Date\((-?\d+)\)\//.exec(value);
  if (jsonDateMatch) {
    const timestamp = Number(jsonDateMatch[1]);
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  const normalizedValue = value.includes('T') ? value : value.replace(/-/g, '/');
  const timestamp = new Date(normalizedValue).getTime();

  return Number.isNaN(timestamp) ? null : timestamp;
}
